import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import { Users, UserCheck } from 'lucide-react';
import { apiService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

const ServiceOpsUserManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [impersonating, setImpersonating] = useState(false);
  const { user, startImpersonation, impersonation } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchServiceOpsUsers();
  }, []);

  const fetchServiceOpsUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const serviceOpsUsers = await apiService.getServiceOpsUsers();
      setUsers(serviceOpsUsers);
    } catch (err) {
      console.error('Error fetching Service Ops users:', err);
      setError('Failed to fetch Service Ops users. Make sure you have the proper permissions.');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = async (targetUser) => {
    try {
      setImpersonating(true);
      setError('');
      
      await startImpersonation(targetUser.id);
      
      // Navigate to the dashboard (where they can access expense reports)
      navigate('/');
      
    } catch (err) {
      console.error('Error starting impersonation:', err);
      setError(err.message || 'Failed to start impersonation');
      setImpersonating(false);
    }
  };

  // Only show this component if user is admin and in Service Ops branch
  if (!user?.is_admin || user?.branch !== 'Service Ops') {
    return (
      <Alert severity="warning">
        This feature is only available to Service Ops administrators.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Users size={28} style={{ marginRight: '12px' }} />
        <Typography variant="h5">Service Ops User Management</Typography>
      </Box>

      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
        Select a Service Ops user to manage their expense reports. You will be able to navigate to their dashboard 
        and create/submit expense reports on their behalf.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {impersonation.isImpersonating && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <UserCheck size={20} style={{ marginRight: '8px' }} />
            Currently managing: <strong>{impersonation.impersonatedUser?.username}</strong>
          </Box>
        </Alert>
      )}

      <Paper>
        {users.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No Service Ops users found.
            </Typography>
          </Box>
        ) : (
          <List>
            {users.map((serviceUser, index) => (
              <React.Fragment key={serviceUser.id}>
                <ListItem disablePadding>
                  <ListItemButton 
                    onClick={() => handleUserSelect(serviceUser)}
                    disabled={impersonating || impersonation.isImpersonating}
                  >
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="h6">
                            {serviceUser.username}
                          </Typography>
                          {impersonation.isImpersonating && impersonation.impersonatedUser?.id === serviceUser.id && (
                            <Chip 
                              label="Currently Managing" 
                              color="primary" 
                              size="small"
                              icon={<UserCheck size={16} />}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {serviceUser.email}
                          </Typography>
                          {serviceUser.vehicle_number && (
                            <Typography variant="body2" color="text.secondary">
                              Vehicle: {serviceUser.vehicle_number}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {index < users.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {users.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Instructions:</strong>
            <br />
            1. Click on a user to start managing their account
            <br />
            2. You'll be redirected to their dashboard where you can create and submit expense reports
            <br />
            3. Use the "Stop Managing User" button to return to your admin dashboard
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ServiceOpsUserManager; 